import { axiosInstance } from "../../Authentication/AxiosInterceptor";

export type LibraryPortfolioCard = {
  id: string;
  title: string;
  access: string;
  isLocked: boolean;
  ownerName: string;
  folderCount: number;
  fileCount: number;
  usedBytes: number;
  canDelete: boolean;
  linkType: string;
  linkId: string;
  linkName: string;
};

export type LibraryLinkOption = {
  type: "Partner" | "Department";
  id: string;
  name: string;
};

export type LibraryHome = {
  storeUsedBytes: number;
  storeLimitBytes: number;
  canCreate: boolean;
  linkOptions: LibraryLinkOption[];
  linkRequired: boolean;
  partnerLabel: string;
  portfolios: LibraryPortfolioCard[];
  archivedPortfolios: LibraryPortfolioCard[];
};

export type LibraryFileNode = {
  id: string;
  title: string;
  extension: string;
  fileSize: number;
  createdAt: string;
  uploadedByName: string;
  tags: string[];
};

export type LibraryFolderNode = {
  id: string;
  title: string;
  folders: LibraryFolderNode[];
  files: LibraryFileNode[];
};

export type LibraryPortfolioView = {
  id: string;
  title: string;
  access: string;
  isLocked: boolean;
  isArchived: boolean;
  ownerName: string;
  isOwner: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canInvite: boolean;
  canUpload: boolean;
  canSetGlobal: boolean;
  canOpenWhenPrivate: boolean;
  linkType?: string;
  linkName?: string;
  partnerLabel?: string;
  folders: LibraryFolderNode[];
};

export type LibraryImplicitAccessUser = {
  sub: string;
  fullName: string;
  email: string;
  roleLabel: string;
};

export type LibraryComment = {
  id: string;
  text: string;
  commenter: string;
  dateMade: string;
};

export type LibraryFileDetails = {
  file: LibraryFileNode;
  comments: LibraryComment[];
  reviewerNames: string[];
  reviewDate: string;
  links: LibraryLinkTarget[];
};

export type LibraryMember = { sub: string; name: string };

export type LibraryMembers = {
  owner: LibraryMember;
  editors: LibraryMember[];
  viewers: LibraryMember[];
};

export type LibraryLinkTarget = {
  type: string;
  areaId: string;
  areaTitle: string;
  questionId: string;
  questionTitle?: string;
  sectionTitle?: string;
  areaLinked?: boolean;
  linked: boolean;
};

export type LibraryLinkTargets = {
  fileId: string;
  areas: LibraryLinkTarget[];
  links: LibraryLinkTarget[];
};

export type LibraryLinkQuestions = {
  fileId: string;
  areaId: string;
  type: string;
  questions: LibraryLinkTarget[];
};

const clientQuery = (clientId: string) => ({ params: { clientId } });

export async function getLibraryHome(clientId: string): Promise<LibraryHome> {
  const response = await axiosInstance.get<LibraryHome>(`LibraryHome/${clientId}`);
  return response.data;
}

export async function getLibraryPortfolio(portfolioId: string, clientId: string): Promise<LibraryPortfolioView> {
  const response = await axiosInstance.get<LibraryPortfolioView>(`LibraryPortfolio/${portfolioId}`, clientQuery(clientId));
  return response.data;
}

export async function createLibraryPortfolio(body: {
  clientId: string;
  title: string;
  access: string;
  linkType?: string;
  linkId?: string;
}): Promise<LibraryPortfolioCard> {
  const response = await axiosInstance.post<LibraryPortfolioCard>("LibraryPortfolio", body);
  return response.data;
}

export async function setLibraryAccess(
  portfolioId: string,
  clientId: string,
  access: "Private" | "Global"
): Promise<LibraryPortfolioView> {
  const response = await axiosInstance.post<LibraryPortfolioView>(
    `LibraryPortfolio/${portfolioId}/access`,
    { access },
    clientQuery(clientId)
  );
  return response.data;
}

export async function getLibraryImplicitAccess(
  portfolioId: string,
  clientId: string
): Promise<LibraryImplicitAccessUser[]> {
  const response = await axiosInstance.get<LibraryImplicitAccessUser[]>(
    `LibraryPortfolio/${portfolioId}/implicit-access`,
    clientQuery(clientId)
  );
  return response.data;
}

export async function patchLibraryPortfolio(
  portfolioId: string,
  clientId: string,
  body: Record<string, unknown>
): Promise<LibraryPortfolioView> {
  const response = await axiosInstance.patch<LibraryPortfolioView>(
    `LibraryPortfolio/${portfolioId}`,
    body,
    clientQuery(clientId)
  );
  return response.data;
}

export async function uploadLibraryFiles(
  portfolioId: string,
  clientId: string,
  folderId: string,
  files: File[],
  replace: boolean
): Promise<LibraryPortfolioView> {
  const form = new FormData();
  form.append("folderId", folderId);
  form.append("replace", replace ? "true" : "false");
  files.forEach((file) => form.append("file", file));
  const response = await axiosInstance.post<LibraryPortfolioView>(
    `LibraryPortfolio/${portfolioId}/files`,
    form,
    { ...clientQuery(clientId), headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

export async function moveLibraryItems(
  clientId: string,
  body: {
    fileIds: string[];
    folderIds: string[];
    destinationPortfolioId: string;
    destinationFolderId: string;
    newTitle?: string;
  }
): Promise<LibraryPortfolioView> {
  const response = await axiosInstance.post<LibraryPortfolioView>("LibraryMove", body, clientQuery(clientId));
  return response.data;
}

export async function deleteLibraryItems(
  portfolioId: string,
  clientId: string,
  fileIds: string[],
  folderIds: string[]
): Promise<LibraryPortfolioView> {
  const response = await axiosInstance.delete<LibraryPortfolioView>(`LibraryPortfolio/${portfolioId}/items`, {
    ...clientQuery(clientId),
    data: { fileIds, folderIds },
  });
  return response.data;
}

export async function deleteLibraryPortfolio(portfolioId: string, clientId: string): Promise<void> {
  await axiosInstance.delete(`LibraryPortfolio/${portfolioId}`, clientQuery(clientId));
}

export type LibraryDirectoryUser = {
  sub: string;
  fullName: string;
  email: string;
  roles?: string[];
};

export async function getLibraryDirectory(portfolioId: string, clientId: string): Promise<LibraryDirectoryUser[]> {
  const response = await axiosInstance.get<LibraryDirectoryUser[]>(
    `LibraryPortfolio/${portfolioId}/directory`,
    clientQuery(clientId)
  );
  return response.data;
}

export async function getLibraryInvitableUsers(portfolioId: string, clientId: string): Promise<LibraryDirectoryUser[]> {
  const response = await axiosInstance.get<LibraryDirectoryUser[]>(
    `LibraryPortfolio/${portfolioId}/invitable-users`,
    clientQuery(clientId)
  );
  return response.data;
}

export async function inviteLibraryUser(
  portfolioId: string,
  clientId: string,
  body: { givenName: string; familyName: string; email: string; role: string }
): Promise<void> {
  await axiosInstance.post(`LibraryPortfolio/${portfolioId}/invite`, body, clientQuery(clientId));
}

export async function getLibraryMembers(portfolioId: string, clientId: string): Promise<LibraryMembers> {
  const response = await axiosInstance.get<LibraryMembers>(`LibraryPortfolio/${portfolioId}/members`, clientQuery(clientId));
  return response.data;
}

export async function changeLibraryMember(
  portfolioId: string,
  clientId: string,
  body: { sub: string; role: string; remove: boolean }
): Promise<LibraryMembers> {
  const response = await axiosInstance.post<LibraryMembers>(
    `LibraryPortfolio/${portfolioId}/members`,
    body,
    clientQuery(clientId)
  );
  return response.data;
}

export async function getLibraryFileDetails(fileId: string, clientId: string): Promise<LibraryFileDetails> {
  const response = await axiosInstance.get<LibraryFileDetails>(`LibraryFile/${fileId}`, clientQuery(clientId));
  return response.data;
}

export async function updateLibraryFileDetails(
  fileId: string,
  clientId: string,
  body: { tags?: string[]; comment?: string }
): Promise<LibraryFileDetails> {
  const response = await axiosInstance.patch<LibraryFileDetails>(`LibraryFile/${fileId}`, body, clientQuery(clientId));
  return response.data;
}

export async function getLibraryLinkTargets(portfolioId: string, fileId: string, clientId: string): Promise<LibraryLinkTargets> {
  const response = await axiosInstance.get<LibraryLinkTargets>(
    `LibraryLinkTargets/${portfolioId}/${fileId}`,
    clientQuery(clientId)
  );
  return response.data;
}

export async function getLibraryLinkQuestions(
  portfolioId: string,
  fileId: string,
  areaId: string,
  clientId: string
): Promise<LibraryLinkQuestions> {
  const response = await axiosInstance.get<LibraryLinkQuestions>(
    `LibraryLinkQuestions/${portfolioId}/${fileId}/${areaId}`,
    clientQuery(clientId)
  );
  return response.data;
}

export async function resolveLibraryLinkTitles(
  portfolioId: string,
  fileId: string,
  clientId: string,
  links: LibraryLinkTarget[]
): Promise<LibraryLinkTarget[]> {
  const areaIds: string[] = [];
  for (const link of links) {
    if (!link.questionId || link.questionTitle?.trim() || !link.areaId) continue;
    if (areaIds.indexOf(link.areaId) === -1) areaIds.push(link.areaId);
  }
  if (areaIds.length === 0) return links;

  const results = await Promise.all(
    areaIds.map((areaId) => getLibraryLinkQuestions(portfolioId, fileId, areaId, clientId))
  );
  const titles = new Map<string, LibraryLinkTarget>();
  for (const result of results) {
    for (const question of result.questions) {
      if (question.questionId) {
        titles.set(`${result.areaId}:${question.questionId}`.toLowerCase(), question);
      }
    }
  }

  return links.map((link) => {
    if (link.questionTitle?.trim()) return link;
    const match = titles.get(`${link.areaId}:${link.questionId}`.toLowerCase());
    if (!match?.questionTitle) return link;
    return {
      ...link,
      questionTitle: match.questionTitle,
      sectionTitle: match.sectionTitle ?? link.sectionTitle ?? "",
    };
  });
}

export async function saveLibraryLink(
  portfolioId: string,
  clientId: string,
  body: { fileId: string; type: string; areaId: string; questionId?: string; linked: boolean }
): Promise<LibraryLinkTargets> {
  const response = await axiosInstance.post<LibraryLinkTargets>(
    "LibraryFileLinks",
    body,
    { params: { clientId, portfolioId } }
  );
  return response.data;
}

export function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}
